import React, { useState, useEffect } from 'react';
import { X, Users2, Pencil, Trash2, Plus, Search, Phone, Mail, Building2 } from 'lucide-react';
import { getSuppliers, Customer } from '../lib/customers';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { toast } from './toasts/SimpleToast';
import CreateSupplierModal from './CreateSupplierModal';

interface SuppliersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SuppliersModal({ isOpen, onClose }: SuppliersModalProps) {
  const { selectedBusiness } = useBusiness();
  const [suppliers, setSuppliers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateSupplierModalOpen, setIsCreateSupplierModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      loadSuppliers();
    }
  }, [isOpen, selectedBusiness?.id]);

  const loadSuppliers = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoading(true);
      const data = await getSuppliers(selectedBusiness.id);
      setSuppliers(data);
      setError(null);
    } catch (err) {
      console.error('Error loading suppliers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSupplier = async (id: string, supplierName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${supplierName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setLoading(true);
      
      if (!supabase) {
        throw new Error('Database connection not available');
      }
      
      // Soft delete by updating status (customers table uses status field)
      const { error: deleteError } = await supabase
        .from('customers')
        .update({ status: 'deleted' })
        .eq('id', id);
      
      if (deleteError) throw deleteError;
      
      await loadSuppliers();
      
      toast.success({
        title: "Supplier deleted",
        description: "The supplier has been deleted successfully.",
        duration: 3000,
      });
    } catch (err) {
      console.error('Error deleting supplier:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete supplier');
    } finally {
      setLoading(false);
    }
  };

  const handleSupplierCreated = (newSupplier: Customer) => {
    setSuppliers(prev => [newSupplier, ...prev]);
    setIsCreateSupplierModalOpen(false);
    toast.success({
      title: "Supplier created",
      description: "The supplier has been created successfully.",
      duration: 3000,
    });
  };

  // Filter suppliers based on search query
  const filteredSuppliers = suppliers.filter(supplier => {
    const searchLower = searchQuery.toLowerCase();
    return (
      supplier.first_name.toLowerCase().includes(searchLower) ||
      supplier.last_name.toLowerCase().includes(searchLower) ||
      (supplier.email || '').toLowerCase().includes(searchLower) ||
      (supplier.phone || '').toLowerCase().includes(searchLower)
    );
  });

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>

          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <Users2 className="h-6 w-6 text-gray-400 mr-3" />
                  <h3 className="text-lg font-medium text-gray-900">Suppliers</h3>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Search and Add Button */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search suppliers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent text-sm"
                  />
                </div>
                <button
                  onClick={() => setIsCreateSupplierModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Supplier
                </button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
              ) : filteredSuppliers.length === 0 ? (
                <div className="text-center py-12">
                  <Users2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {searchQuery ? 'No suppliers found' : 'No suppliers yet'}
                  </h3>
                  <p className="text-gray-500 mb-6">
                    {searchQuery 
                      ? 'Try adjusting your search criteria' 
                      : 'Add your first supplier to get started'
                    }
                  </p>
                  {!searchQuery && (
                    <button
                      onClick={() => setIsCreateSupplierModalOpen(true)}
                      className="inline-flex items-center px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Supplier
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                  {filteredSuppliers.map(supplier => (
                    <div key={supplier.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between">
                        <h3 className="text-lg font-medium text-gray-900">
                          {supplier.first_name} {supplier.last_name}
                        </h3>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleDeleteSupplier(supplier.id, `${supplier.first_name} ${supplier.last_name}`)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      
                      {supplier.type === 'business' && (
                        <div className="flex items-center text-sm text-gray-600 mt-2">
                          <Building2 className="h-4 w-4 mr-2 text-gray-400" />
                          Business Supplier
                        </div>
                      )}
                      
                      {supplier.email && (
                        <div className="flex items-center text-sm text-gray-600 mt-2">
                          <Mail className="h-4 w-4 mr-2 text-gray-400" />
                          {supplier.email}
                        </div>
                      )}
                      
                      {supplier.phone && (
                        <div className="flex items-center text-sm text-gray-600 mt-2">
                          <Phone className="h-4 w-4 mr-2 text-gray-400" />
                          {supplier.phone}
                        </div>
                      )}

                      {supplier.address && (
                        <div className="flex items-start text-sm text-gray-600 mt-2">
                          <Building2 className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0 mt-0.5" />
                          <span className="break-words">{supplier.address}</span>
                        </div>
                      )}

                      {supplier.notes && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-md">
                          <p className="text-sm text-gray-700">{supplier.notes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Supplier Modal */}
      <CreateSupplierModal
        isOpen={isCreateSupplierModalOpen}
        onClose={() => setIsCreateSupplierModalOpen(false)}
        onSuccess={handleSupplierCreated}
        businessId={selectedBusiness?.id || ''}
      />
    </>
  );
} 